import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { type MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";

import {
  ESTADO_PAGO_PREAGENDAMIENTO_PYL,
  ESTADO_PREAGENDAMIENTO_PYL,
  EstadosPreagendamientoEnumChoice,
  calcColorStatePreAgenda,
  calcStateTextPreAgenda,
  cellLineaRazonSocial,
  dateCellValues,
  emptyCellOneLevel,
  formatCurrency,
  formatDateWithTimeAMPM,
  formatDateWithTimeCell,
} from "@/shared";
import { EstadoComponente, SingleButton } from "@/shared/components/ui";
import { SingleImageAndBtnCellModal } from "@/shared/components/ui/Dialogs";
import {
  EstadoPagoEnum,
  EstadosPreagendamientoCodigoEnum,
  IPreagendamiento,
} from "@/shared/interfaces";
import {
  EncuestaPreagendaCell,
  EquipoAdicionalIconBtnCellModal,
} from "../components/tables";
// import InfoUser from '@/shared/components/ui/InfoUser/InfoUser';
import dayjs from "dayjs";
import OSData from "@/shared/components/ui/OSData/OSData";
import InfoObservaciones from "@/erp/sale/asesor/pre_agendamiento/shared/componente/InfoObservaciones/InfoObservaciones";

export const useColumnsPreagendamientos = () => {
  const preagendamientoBaseColumns = useMemo<MRT_ColumnDef<IPreagendamiento>[]>(
    () => [
      {
        accessorKey: "identificacion",
        header: "identificacion",
        size: 180,
        Cell: ({ row }) =>
          row.original?.prospecto_data?.identificacion || "N/A",
      },
      {
        accessorKey: "razon_social",
        header: "Cliente",
        size: 300,
        Cell: ({ row }) => row.original?.prospecto_data?.razon_social || "N/A",
      },

      // contrato ------------------
      {
        accessorKey: "numero_contrato",
        header: "Núm. Contrato",
        size: 240,
        Cell: ({ row }) =>
          row.original?.contrato_data?.numero_referencia || "N/A",
      },
      {
        accessorKey: "url_contrato",
        header: "CONTRATO",
        size: 100,
        Cell: ({ row }) => {
          const url = row.original?.contrato_data?.url_contrato;
          if (!url) return "N/A";

          return (
            <SingleButton
              newCustomButton
              iconButton
              label="Ver contrato"
              color="error"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => {
                window.open(url, "_blank");
              }}
            />
          );
        },
      },

      /// estado_pyl ------------------
    ],
    []
  );

  const preagendaPyLStateOnlyColumns = useMemo<
    MRT_ColumnDef<IPreagendamiento>[]
  >(
    () => [
      /// estado_pyl ------------------
      {
        accessorKey: "estado_pyl",
        header: "estado pyl",
        size: 150,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <EstadoComponente
            backgroundColor={calcColorStatePreAgenda(
              row.original
                ?.estado_preagendamiento as EstadosPreagendamientoCodigoEnum
            )}
            texto={calcStateTextPreAgenda(
              row.original
                ?.estado_preagendamiento as EstadosPreagendamientoEnumChoice
            )}
          />
        ),
      },
    ],
    []
  );
  const preagendaMotivoRechazoColumns = useMemo<
    MRT_ColumnDef<IPreagendamiento>[]
  >(
    () => [
      {
        accessorKey: "motivo_rechazo",
        header: "Motivo de rechazo",
        size: 200,
        Cell: ({ row }) => row.original?.motivo_rechazo_data?.nombre || "N/A",
      },
    ],
    []
  );

  const preagendaBaseColumns02 = useMemo<MRT_ColumnDef<IPreagendamiento>[]>(
    () => [
      ////* estado_pago ------------------
      {
        accessorKey: "estado_pago",
        header: "estado pago",
        size: 150,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const isPendingVoucher =
            row.original?.estado_pago === EstadoPagoEnum.VOUCHER_PENDIENTE;
          return (
            <EstadoComponente
              backgroundColor={isPendingVoucher ? "blue" : "green"}
              texto={row.original?.estado_pago || "N/A"}
            />
          );
        },
      },
      {
        accessorKey: "descripcion_pago",
        header: "Descripción de pago",
        size: 300,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => emptyCellOneLevel(row, "descripcion_pago"),
      },

      {
        accessorKey: "direccion",
        header: "direccion",
        size: 300,
        Cell: ({ row }) => row.original?.preventa_data?.direccion || "N/A",
      },
      {
        accessorKey: "fecha_instalacion",
        header: "Fecha Instalación",
        size: 300,
        Cell: ({ row }) => dateCellValues(row, "fecha_instalacion"),
      },
      {
        accessorKey: "hora_instalacion",
        header: "Hora Instalación",
        size: 300,
        Cell: ({ row }) => emptyCellOneLevel(row, "hora_instalacion"),
      },

      //
      {
        accessorKey: "coordenadas",
        header: "COORDENADAS",
        size: 180,

        Cell: ({ row }) => emptyCellOneLevel(row, "coordenadas"),
      },
      {
        accessorKey: "celular",
        header: "CELULAR CLIENTE",
        size: 180,

        Cell: ({ row }) => row.original?.prospecto_data?.celular || "N/A",
      },
      {
        accessorKey: "celular_2",
        header: "CELULAR 2",
        size: 180,

        Cell: ({ row }) => row.original?.preventa_data?.celular_2 || "N/A",
      },
      {
        accessorKey: "email",
        header: "EMAIL",
        size: 180,
        Cell: ({ row }) => row.original?.preventa_data?.email || "N/A",
      },

      // Persona de referencia ------------------
      {
        accessorKey: "persona_referencia_nombre",
        header: "Persona de referencia",
        size: 210,
        Cell: ({ row }) =>
          row.original?.preventa_data?.persona_referencia_nombre || "N/A",
      },
      {
        accessorKey: "persona_referencia_celular",
        header: "Celular de referencia",
        size: 210,
        Cell: ({ row }) =>
          row.original?.preventa_data?.persona_referencia_celular || "N/A",
      },
      {
        accessorKey: "persona_referencia_celular_2",
        header: "Celular de referencia 2",
        size: 210,
        Cell: ({ row }) =>
          row.original?.preventa_data?.persona_referencia_celular_2 || "N/A",
      },
      {
        accessorKey: "persona_referencia_parentesco",
        header: "Parentesco de referencia",
        size: 210,
        Cell: ({ row }) =>
          row.original?.preventa_data?.persona_referencia_parentesco || "N/A",
      },

      ///* fk ========================================
      // // plan ------------------
      {
        accessorKey: "tipo_plan",
        header: "Tipo Plan",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.preventa_data?.tipo_plan || "N/A";
        },
      },
      {
        accessorKey: "tipo_servicio",
        header: "Tipo de servicio",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.preventa_data?.tipo_servicio || "N/A";
        },
      },
      {
        accessorKey: "plan",
        header: "Plan de internet",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.plan_data?.nombre || "N/A";
        },
      },
      {
        accessorKey: "velocidad_internet",
        header: "Velocidad de internet",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.preventa_data?.velocidad
            ? `${row.original?.preventa_data?.velocidad} ${
                row.original?.plan_data?.unidad || ""
              }`
            : "N/A";
        },
      },
      {
        accessorKey: "valor_plan",
        header: "Valor del plan",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.preventa_data?.valor_plan
            ? formatCurrency(row.original?.preventa_data?.valor_plan)
            : formatCurrency(0);
        },
      },

      // valor_instalacion ------------------
      {
        accessorKey: "valor_instalacion",
        header: "Valor Instalación",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.preventa_data?.valor_instalacion
            ? formatCurrency(row.original?.preventa_data?.valor_instalacion)
            : formatCurrency(0);
        },
      },
      {
        accessorKey: "valor_instalacion_iva",
        header: "Valor IVA Instalación",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.preventa_data?.valor_instalacion_iva
            ? formatCurrency(row.original?.preventa_data?.valor_instalacion_iva)
            : formatCurrency(0);
        },
      },
      {
        accessorKey: "valor_instalacion_total",
        header: "Valor Total Instalación",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.preventa_data?.valor_instalacion_total
            ? formatCurrency(
                row.original?.preventa_data?.valor_instalacion_total
              )
            : formatCurrency(0);
        },
      },

      // flota & nap ------------------
      {
        accessorKey: "flota",
        header: "FLOTA",
        size: 180,
        Cell: ({ row }) => row.original?.flota_data?.nombre || "N/A",
      },
      {
        accessorKey: "nap_data_nombre",
        header: "NAP",
        size: 180,
        Cell: ({ row }) => row.original?.nap_data_nombre || "N/A",
      },
      {
        accessorKey: "distancia",
        header: "DISTANCIA A LA NAP",
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, "distancia"),
      },

      // encuesta y usos ------------------
      {
        accessorKey: "encuesta",
        header: "Encuesta",
        size: 81,
        Cell: ({ row }) => {
          return (
            <EncuestaPreagendaCell encuesta={row.original?.encuesta as any} />
          );
        },
      },

      // equipos_adicionales ------------------
      {
        accessorKey: "equipos_adicionales",
        header: "Equipos adicionales",
        size: 180,
        Cell: ({ row }) => (
          <EquipoAdicionalIconBtnCellModal
            equipoAdicional={row.original.preventa_data?.equipos_adicionales}
          />
        ),
      },

      // segmentacion ------------------
      {
        accessorKey: "segmentacion",
        header: "SEGMENTACIÓN",
        size: 180,
        Cell: ({ row }) => row.original?.prospecto_data?.segmentacion || "N/A",
      },
      {
        accessorKey: "puntaje_score",
        header: "PUNTAJE SCORE",
        size: 180,
        Cell: ({ row }) => row.original?.prospecto_data?.puntaje_score || "N/A",
      },
    ],
    []
  );

  const preagendamientoBaseColumnsEnd = useMemo<
    MRT_ColumnDef<IPreagendamiento>[]
  >(
    () => [
      ///* photos ------------------
      {
        accessorKey: "url_foto_identificacion_delantera",
        header: "FOTO IDENTIFICACION DELANTERA",
        size: 180,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => {
          return (
            <SingleImageAndBtnCellModal
              url={
                row.original?.preventa_data?.url_foto_identificacion_delantera
              }
              title="Foto de identificación delantera"
            />
          );
        },
      },
      {
        accessorKey: "url_foto_identificacion_posterior",
        header: "FOTO IDENTIFICACION POSTERIOR",
        size: 180,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => {
          return (
            <SingleImageAndBtnCellModal
              url={
                row.original?.preventa_data?.url_foto_identificacion_posterior
              }
              title="Foto de identificación posterior"
            />
          );
        },
      },
      {
        accessorKey: "url_foto_aceptacion",
        header: "FOTO ACEPTACION CLIENTE",
        size: 180,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => {
          return (
            <SingleImageAndBtnCellModal
              url={row.original?.preventa_data?.url_foto_aceptacion!}
              title="Foto de Acetación Cliente"
            />
          );
        },
      },

      {
        accessorKey: "preventa_data.url_foto_cuenta_bancaria",
        header: "FOTO CUENTA BANCARIA",
        size: 180,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => {
          return (
            <SingleImageAndBtnCellModal
              url={row.original?.preventa_data?.url_foto_cuenta_bancaria!}
              title="Foto de la cuenta bancaria"
            />
          );
        },
      },
      {
        accessorKey: "preventa_data.url_foto_tarjeta",
        header: "FOTO TARJETA",
        size: 180,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => {
          return (
            <SingleImageAndBtnCellModal
              url={row.original?.preventa_data?.url_foto_tarjeta!}
              title="Foto de la tarjeta"
            />
          );
        },
      },
      {
        accessorKey: "url_foto_comprobante_v1",
        header: "FOTO COMPROBANTE PAGO",
        size: 180,
        enableColumnFilter: true,
        enableSorting: true,
        Cell: ({ row }) => {
          return (
            <SingleImageAndBtnCellModal
              url={row.original?.url_foto_comprobante!}
              title="Foto del comprobante de pago"
            />
          );
        },
      },

      ///* trazabilidad ------------------
      {
        accessorKey: "vendedor",
        header: "Asesor Venta",
        size: 180,
        Cell: ({ row }) => row.original.user_create
      },
      {
        accessorKey: "fecha_venta",
        header: "Fecha de venta",
        size: 180,
        Cell: ({ row }) => formatDateWithTimeAMPM(row.original.fecha_creacion)
      },
      {
        accessorKey: "fecha_creacion",
        header: "Fecha de Creación",
        size: 200,
        enableColumnFilter: true,
        filterVariant: "date",
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, "fecha_creacion"),
      },
      {
        accessorKey: "fecha_modificacion",
        header: "Fecha de Modificación",
        size: 200,
        enableColumnFilter: true,
        filterVariant: "date",
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, "fecha_modificacion"),
      },
    ],
    []
  );

  ///* columns ------------------------------
  //columna con los cambios solicitados 26/06/2024
  const preagendamientoColumnBaseCambios = useMemo<
    MRT_ColumnDef<IPreagendamiento>[]
  >(
    () => [
      {
        accessorKey: "identificacion",
        header: "Identificación",
        size: 50,
        Cell: ({ row }) => row.original?.prospecto_data?.identificacion,
      },
      {
        accessorKey: "cliente_razon_social",
        header: "Cliente",
        size: 300,
        Cell: ({ row }) =>
          cellLineaRazonSocial(
            row.original?.contrato_data,
            row.original.prospecto_data?.razon_social!
          ),
      },
      {
        accessorKey: "fecha_creacion",
        header: "Fecha de Preagenda",
        size: 200,
        // enableColumnFilter: true,
        // filterVariant: 'date',
        // enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, "fecha_creacion"),
      },
      {
        accessorKey: "fecha_modificacion",
        header: "Fecha de Última Modificación",
        size: 200,
        // enableColumnFilter: true,
        // filterVariant: 'date',
        // enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, "fecha_modificacion"),
      },
      {
        accessorKey: "fecha_instalacion",
        header: "Fecha instalación",
        size: 180,
      },
      {
        accessorKey: "hora_instalacion",
        header: "Hora instalación",
        size: 180,
      },
      {
        accessorKey: "ciudad_nombre", //ciudad_data.nombre
        header: "Ciudad",
        size: 200,
        Cell: ({ row }) => row.original?.ciudad_data?.nombre || "N/A",
        // Cell: ({ row }) => row.original?.ciudad_data?.nombre || 'N/A',
      },
      {
        accessorKey: "flota_nombre",
        header: "FLOTA",
        size: 180,
        Cell: ({ row }) => row.original?.flota_data?.nombre || "N/A",
      },

      /*  {
         accessorKey: 'ingreso_salida',
         header: 'Ingreso/Salida del Tecnico',
         size: 180,
         Cell: ({ row }) => 'N/A',
       }, */
      {
        accessorKey: "coordenadas",
        header: "COORDENADAS",
        size: 180,

        Cell: ({ row }) => emptyCellOneLevel(row, "coordenadas"),
      },
      {
        accessorKey: "celular",
        header: "CELULAR CLIENTE",
        size: 180,

        Cell: ({ row }) => row.original?.prospecto_data?.celular || "N/A",
      },
      {
        accessorKey: "celular_2",
        header: "CELULAR 2",
        size: 180,

        Cell: ({ row }) => row.original?.preventa_data?.celular_2 || "N/A",
      },
      {
        accessorKey: "estado_preagendamiento",
        header: "estado pyl",
        size: 150,
        // enableColumnFilter: false,
        filterVariant: "select",
        filterSelectOptions: ESTADO_PREAGENDAMIENTO_PYL,
        enableSorting: false,
        Cell: ({ row }) => (
          <EstadoComponente
            backgroundColor={calcColorStatePreAgenda(
              row.original
                ?.estado_preagendamiento as EstadosPreagendamientoCodigoEnum
            )}
            texto={calcStateTextPreAgenda(
              row.original
                ?.estado_preagendamiento as EstadosPreagendamientoEnumChoice
            )}
          />
        ),
      },
      {
        accessorKey: "estado_pago",
        header: "estado pago",
        size: 150,
        // enableColumnFilter: false,
        filterVariant: "select",
        filterSelectOptions: ESTADO_PAGO_PREAGENDAMIENTO_PYL,
        enableSorting: false,
        Cell: ({ row }) => {
          const isPendingVoucher =
            row.original?.estado_pago === EstadoPagoEnum.VOUCHER_PENDIENTE;
          return (
            <EstadoComponente
              backgroundColor={isPendingVoucher ? "blue" : "green"}
              texto={row.original?.estado_pago || "N/A"}
            />
          );
        },
      },
      {
        accessorKey: "fecha_aceptacion",
        header: "fecha de aceptación",
        size: 300,
        Cell: ({ row }) =>
          row.original?.preventa_data?.fecha_aceptacion !== null
            ? dayjs(row.original?.preventa_data?.fecha_aceptacion).format(
                "L LT"
              )
            : "N/A",
      },
      {
        accessorKey: "direccion",
        header: "direccion",
        size: 300,
        Cell: ({ row }) => row.original?.preventa_data?.direccion || "N/A",
      },
      {
        accessorKey: "nap",
        header: "NAP",
        size: 180,
        Cell: ({ row }) => row.original?.nap_data_nombre || "N/A",
      },
      {
        accessorKey: "distancia",
        header: "DISTANCIA A LA NAP",
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, "distancia"),
      },
      {
        accessorKey: "contrato_numero",
        header: "Núm. Contrato",
        size: 240,
        Cell: ({ row }) =>
          row.original?.contrato_data?.numero_referencia || "N/A",
      },
      {
        accessorKey: "contrato_tipo_plan",
        header: "Tipo Plan",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.preventa_data?.tipo_plan || "N/A";
        },
      },
      {
        accessorKey: "equipos_adicionales",
        header: "Equipos adicionales",
        size: 180,
        Cell: ({ row }) => (
          <EquipoAdicionalIconBtnCellModal
            equipoAdicional={row.original.preventa_data?.equipos_adicionales}
          />
        ),
      },
      {
        accessorKey: "valor_instalacion_total",
        header: "Valor Total Instalación",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.preventa_data?.valor_instalacion_total
            ? formatCurrency(
                row.original?.preventa_data?.valor_instalacion_total
              )
            : formatCurrency(0);
        },
      },
      {
        accessorKey: "plan",
        header: "Plan de internet",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.plan_data?.nombre || "N/A";
        },
      },
      {
        accessorKey: "tipo_servicio",
        header: "Tipo de servicio",
        size: 210,
        Cell: ({ row }) => {
          return row.original?.preventa_data?.tipo_servicio || "N/A";
        },
      },
      // {
      //   accessorKey: 'supervisor_data.razon_social',
      //   header: 'Supervisor venta',
      //   Cell: ({ row }) =>
      //     row.original.supervisor_data != null ?
      //       <InfoUser
      //         user={row.original?.supervisor_data.user}
      //       />
      //       : 'N/A'
      //   ,
      // },
      // {
      //   accessorKey: 'canal_venta_create',
      //   header: 'canal venta',
      // },
      /*  ...preagendamientoBaseColumns,
       ...preagendaPyLStateOnlyColumns,
       ...preagendaBaseColumns02,
       ...preagendamientoBaseColumnsEnd, */
    ],
    [preagendamientoBaseColumns, preagendamientoBaseColumnsEnd]
  );

  const preagendamientoPendingReviewColumns = useMemo<
    MRT_ColumnDef<IPreagendamiento>[]
  >(
    () => [
      ...preagendamientoColumnBaseCambios,
      {
        accessorKey: "observaciones.observacion_llamada",
        header: "Observaciones",
        Cell: ({ row }) => (
          <InfoObservaciones
            label={"Observación de Llamada"}
            contentText={row.original?.observaciones?.observacion_llamada}
            fechaMod={
              row.original?.fecha_modificacion
                ? dayjs(row.original?.fecha_modificacion).format("LLL")
                : ""
            }
          />
        ),
      },

      /* 
      ...preagendamientoBaseColumnsEndFechas,
      ...preagendamientoBaseColumns,
      ...preagendaPyLStateOnlyColumns,
      ...preagendaMotivoRechazoColumns,
      ...preagendaBaseColumns02,
      ...preagendamientoBaseColumnsEnd, */
    ],
    []
  );

  const preagendamientoPendingReviewColumnsOcupado = useMemo<
    MRT_ColumnDef<IPreagendamiento>[]
  >(
    () => [
      ...preagendamientoColumnBaseCambios,
      {
        accessorKey: "user_gestionando",
        header: "Gestionado por",
        size: 180,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => row.original?.user_gestionando || "Libre",
      },
      {
        accessorKey: "preventa_data.os_aceptacion",
        header: "sustento de aceptación",
        size: 180,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <OSData os_data={row.original?.preventa_data?.os_aceptacion!} />
        ),
      },

      /* ...preagendamientoBaseColumns,
      ...preagendaPyLStateOnlyColumns,
      ...preagendaMotivoRechazoColumns,
      ...preagendaBaseColumns02,*/
    ],
    []
  );

  const preagendaRejectedColumns = useMemo<MRT_ColumnDef<IPreagendamiento>[]>(
    () => [
      /* ...preagendamientoBaseColumns,
      ...preagendaPyLStateOnlyColumns, */
      ...preagendaMotivoRechazoColumns,
      ...preagendamientoColumnBaseCambios,
      /* ...preagendaBaseColumns02,
      ...preagendamientoBaseColumnsEnd, */
    ],
    [preagendamientoBaseColumns, preagendamientoBaseColumnsEnd]
  );

  const preagendasUpdatedBySellerColumns = useMemo<
    MRT_ColumnDef<IPreagendamiento>[]
  >(
    () => [
      ...preagendamientoColumnBaseCambios,
      /* ...preagendamientoBaseColumns,
      ...preagendaPyLStateOnlyColumns,
      ...preagendaMotivoRechazoColumns, */

      {
        accessorKey: "revisado_pyl",
        header: "Estado Revisión",
        size: 150,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const isReviewed = !!row.original?.revisado_pyl;

          return (
            <EstadoComponente
              backgroundColor={isReviewed ? "green" : "blue"}
              texto={isReviewed ? "REVISADO POR PYL" : "PENDIENTE DE REVISIÓN"}
            />
          );
        },
      },
      {
        accessorKey: "fecha_revision_pyl",
        header: "Fecha de revisión",
        size: 200,
        Cell: ({ row }) => formatDateWithTimeCell(row, "fecha_revision_pyl"),
      },
      /* ...preagendaBaseColumns02,
      ...preagendamientoBaseColumnsEnd, */
    ],
    []
  );

  return {
    preagendaPyLStateOnlyColumns,
    preagendaBaseColumns02,

    preagendamientoPendingReviewColumns,
    preagendamientoPendingReviewColumnsOcupado,
    preagendaRejectedColumns,
    preagendasUpdatedBySellerColumns,
  };
};
